import { Box, CircularProgress, Grid, Stack, styled, Typography } from "@mui/material";

import { background, black, button, divider, orange } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Textarea } from "@/components/elements/Textarea";
import { Button } from "@/components/elements/Button";
import { Progress } from "@/components/elements/Progress";
import { IconSaveScanImage } from "@/assets/icons";
import { DropZoneScan } from "@/features/talent/components/talent-scan-image/DropZoneScan";
import { UseScanImageStateReturn } from "@/features/talent/components/talent-scan-image/useScanImageState";

type Props = {
  state: UseScanImageStateReturn;
};

export function ScanUploadStep({ state }: Props) {
  const { methods, control, scanImageSchema, isSubmit, processingProgress, handleChangeFiles, onClose, onSubmit, cancelScan } = state;

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={scanImageSchema}>
      {isSubmit && (
        <Box sx={{ px: 3, pt: 6 }}>
          <ProcessingProgressBar value={processingProgress} />
        </Box>
      )}
      {isSubmit ? (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} height={60}>
          <CircularProgress sx={{ color: black[90] }} size={20} />
          <Typography variant="body14Semi" textAlign="center">
            処理中（1分程度かかる場合がございます）
          </Typography>
        </Stack>
      ) : (
        <Box sx={{ overflowY: "auto", maxHeight: "calc(90vh - 56px - 72px)" }}>
          <Stack sx={{ p: 4, gap: "10px" }}>
            <Typography variant="body14Light">
              面談メモ、履歴書、在留カードなどの人材情報を含むデータからAIによる取り込みができます。メモまたは書類のどちらかを入力ください。
            </Typography>
            <Typography variant="body14Light" sx={{ color: orange[80] }}>
              マイナンバーに統合された特定在留カードの裏面にはマイナンバーが記載されているためアップロードはしないでください。
            </Typography>
            <Typography variant="body14Light">マイナンバーの取扱いについては個人情報保護委員会が定めるガイドラインをご確認ください</Typography>
            <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
              <Grid item xs={12}>
                <FormField control={control} name="memo" label="人材情報や面談などのメモ">
                  <Textarea placeholder="入力してください" />
                </FormField>
              </Grid>
              <Grid item xs={12}>
                <DropZoneScan handleChangeFiles={handleChangeFiles} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="cap12" sx={{ color: orange[80], mt: 1 }}>
                  このAIの取り込みには少し時間がかかりますので、しばらくお待ちください。
                </Typography>
              </Grid>
            </Grid>
          </Stack>
        </Box>
      )}

      <Box height={69}>
        <hr color={divider.high} />
        <Stack direction="row" gap="16px" justifyContent="space-between" p="16px 32px">
          <Button size="sm" variant="ghost" onClick={onClose}>
            キャンセル
          </Button>
          {isSubmit ? (
            <Button size="sm" variant="outline" disabled onClick={cancelScan}>
              ドキュメントを処理中...
            </Button>
          ) : (
            <Button size="sm" variant="primary" type="submit" startIcon={<IconSaveScanImage />}>
              AI取り込みを開始
            </Button>
          )}
        </Stack>
      </Box>
    </Form>
  );
}

const ProcessingProgressBar = styled(Progress)`
  &.MuiLinearProgress-root {
    height: 8px;
    border-radius: ${rounded.xs};
    background-color: ${background.disable};
    .MuiLinearProgress-bar {
      background-color: ${button.primary};
      transition: transform 0.5s ease-out;
    }
  }
`;
